import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, LogOut, Search, Filter, RefreshCw, Eye, Trash2, Check, X, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const statusConfig = {
    pending: { label: "Pending", color: "bg-amber-100 text-amber-800", icon: <Clock className="h-4 w-4" /> },
    in_review: { label: "In Review", color: "bg-blue-100 text-blue-800", icon: <Eye className="h-4 w-4" /> },
    approved: { label: "Approved", color: "bg-emerald-100 text-emerald-800", icon: <Check className="h-4 w-4" /> },
    completed: { label: "Completed", color: "bg-slate-200 text-slate-800", icon: <Check className="h-4 w-4" /> },
    rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: <X className="h-4 w-4" /> },
  };

  const cargoTypeLabels = {
    general: "General Freight",
    palletized: "Palletized",
    boxes: "Boxes/Packages",
    furniture: "Furniture",
    equipment: "Industrial Equipment",
    other: "Other",
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      await axios.post(`${API_URL}/api/admin/login`, { password });
      setIsAuthenticated(true);
      toast.success("Login successful");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-requests`, {
          params: statusFilter !== "all" ? { status: statusFilter } : {},
        }),
        axios.get(`${API_URL}/api/service-requests-stats`),
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/service-requests/${requestId}/status`, { status: newStatus });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    try {
      await axios.delete(`${API_URL}/api/service-requests/${requestId}`);
      toast.success("Request deleted");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      toast.error("Error deleting request");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, statusFilter]);

  const filteredRequests = requests.filter(
    (req) =>
      req.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="admin-login-page">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="bg-orange-600 p-2 rounded-sm">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              KDA TRUCKING ADMIN
            </span>
          </div>
          <form onSubmit={handleLogin}>
            <Label htmlFor="password" className="input-label">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="h-12 mb-6"
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold uppercase tracking-wider py-4"
              data-testid="admin-login-button"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 sticky top-0 z-30">
        <div className="section-container flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-orange-600 p-2 rounded-sm">
              <Truck className="h-5 w-5" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              KDA TRUCKING ADMIN
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => setIsAuthenticated(false)}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="section-container py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="stats-cards">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-amber-600">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-600">In Review</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{stats.in_review}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-emerald-600">Approved</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-emerald-600">{stats.approved}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-slate-600">{stats.completed}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-600">Rejected</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between" data-testid="filters-section">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid="requests-table">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Route</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-500">
                      {loading ? "Loading..." : "No requests found"}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} data-testid={`request-row-${request.id}`}>
                      <td className="whitespace-nowrap">
                        {format(new Date(request.created_at), "MMM dd, yyyy")}
                      </td>
                      <td className="font-medium text-slate-900">{request.company_name}</td>
                      <td>
                        <div>{request.contact_name}</div>
                        <div className="text-xs text-slate-500">{request.email}</div>
                      </td>
                      <td className="whitespace-nowrap">
                        {request.origin_city}, {request.origin_state.slice(0, 2).toUpperCase()} →{" "}
                        {request.destination_city}, {request.destination_state.slice(0, 2).toUpperCase()}
                      </td>
                      <td>{cargoTypeLabels[request.cargo_type] || request.cargo_type}</td>
                      <td>
                        <Badge className={statusConfig[request.status]?.color}>
                          {statusConfig[request.status]?.label || request.status}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setDetailsOpen(true);
                            }}
                            data-testid={`view-details-${request.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`status-menu-${request.id}`}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => updateStatus(request.id, "pending")}>
                                Pending
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(request.id, "in_review")}>
                                In Review
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(request.id, "approved")}>
                                Approved
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(request.id, "completed")}>
                                Completed
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(request.id, "rejected")}>
                                Rejected
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteRequest(request.id)}
                            data-testid={`delete-${request.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl" data-testid="details-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Request Details</DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <span>ID: {selectedRequest.id}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase">Company</p>
                  <p className="text-slate-900">{selectedRequest.company_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase">Contact</p>
                  <p className="text-slate-900">{selectedRequest.contact_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase">Email</p>
                  <p className="text-slate-900">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 uppercase">Phone</p>
                  <p className="text-slate-900">{selectedRequest.phone}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Origin</p>
                    <p className="text-slate-900">{selectedRequest.origin_city}, {selectedRequest.origin_state}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Destination</p>
                    <p className="text-slate-900">{selectedRequest.destination_city}, {selectedRequest.destination_state}</p>
                  </div>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Cargo Type</p>
                    <p className="text-slate-900">{cargoTypeLabels[selectedRequest.cargo_type] || selectedRequest.cargo_type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Estimated Weight</p>
                    <p className="text-slate-900">{selectedRequest.estimated_weight ? `${selectedRequest.estimated_weight} lbs` : "Not specified"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Pickup Date</p>
                    <p className="text-slate-900">{selectedRequest.pickup_date}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase">Status</p>
                    <Badge className={statusConfig[selectedRequest.status]?.color}>
                      {statusConfig[selectedRequest.status]?.label || selectedRequest.status}
                    </Badge>
                  </div>
                </div>
              </div>
              {selectedRequest.cargo_description && (
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-2">Cargo Description</p>
                  <p className="text-slate-900">{selectedRequest.cargo_description}</p>
                </div>
              )}
              {selectedRequest.special_requirements && (
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-slate-500 uppercase mb-2">Special Requirements</p>
                  <p className="text-slate-900">{selectedRequest.special_requirements}</p>
                </div>
              )}
              <div className="border-t pt-4 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => deleteRequest(selectedRequest.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={() => setDetailsOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
